import { Component, Input } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { EMPTY_VALUE, fmtUtcDateTime } from 'src/app/_helpers/utils';
import { CancellationFlowHistoryEntry } from 'src/app/_types/cancellation.types';
import {
  getCancellationFlowHistoryLabel,
  getCancellationFlowHistoryStatusClass,
} from 'src/app/enums/cancellation/cancellation-history-status.enum';
import { BadgeRendererComponent } from 'src/app/shared/components/ag-grid-renderers/badge-renderer/badge-renderer.component';

import { describeChange } from './cancellation-log-change';
import { CancellationLogChangesRendererComponent } from './cancellation-log-changes-renderer.component';

const statusLabel = (entry?: CancellationFlowHistoryEntry): string =>
  entry?.status ? getCancellationFlowHistoryLabel(entry.status) : EMPTY_VALUE;

/**
 * The timeline as a table, newest first: status periods and `UPDATED` edits
 * alike. The "Modifications" column spells out an edit's field changes in the
 * row itself, in the words the rest of the page uses; see {@link
 * describeChange}. The flow's log table is not reused — its columns are the
 * flow's — but the layout follows it, down to "Type EVT" and "Statut" both
 * naming the row's status, the former as text and the latter as a badge.
 */
@Component({
  selector: 'app-cancellation-log-table',
  standalone: true,
  imports: [AgGridModule],
  template: `
    <div class="dashboard-table mt-8">
      <ag-grid-angular
        style="width: 100%"
        class="ag-theme-alpine lty-table"
        [rowData]="entries"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [domLayout]="'autoHeight'"
        [pagination]="false"
        [animateRows]="false"
        [suppressRowHoverHighlight]="true"
        [suppressRowTransform]="true"
        [suppressRowClickSelection]="true"
      />
    </div>
  `,
})
export class CancellationLogTableComponent {
  @Input() entries: CancellationFlowHistoryEntry[] = [];

  protected readonly columnDefs: ColDef<CancellationFlowHistoryEntry>[] = [
    {
      headerName: $localize`Type EVT`,
      field: 'status',
      cellClass: 'simple-text-cell',
      minWidth: 170,
      valueGetter: (params) => statusLabel(params.data),
    },
    {
      headerName: $localize`Date`,
      field: 'updatedOn',
      minWidth: 150,
      valueGetter: (params) => fmtUtcDateTime(params.data?.updatedOn),
    },
    {
      headerName: $localize`Par`,
      field: 'updatedByFullName',
      cellClass: 'simple-text-cell',
      minWidth: 150,
      valueGetter: (params) =>
        params.data?.updatedByFullName?.trim() ||
        params.data?.updatedBy ||
        EMPTY_VALUE,
    },
    {
      headerName: $localize`Statut`,
      field: 'status',
      minWidth: 170,
      valueGetter: (params) => statusLabel(params.data),
      cellRenderer: BadgeRendererComponent,
      cellRendererParams: {
        badgeClass: (data: CancellationFlowHistoryEntry) =>
          getCancellationFlowHistoryStatusClass(data.status),
      },
    },
    {
      headerName: $localize`Modifications`,
      field: 'changes',
      cellClass: 'simple-text-cell',
      minWidth: 260,
      flex: 2,
      valueGetter: (params) =>
        (params.data?.changes ?? []).map(describeChange).join('; ') ||
        EMPTY_VALUE,
      cellRenderer: CancellationLogChangesRendererComponent,
    },
  ];

  protected readonly defaultColDef: ColDef<CancellationFlowHistoryEntry> = {
    filter: false,
    sortable: false,
    resizable: true,
    autoHeight: true,
    flex: 1,
  };
}
